
import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useMapboxToken } from '@/contexts/MapboxTokenContext';
import { ExternalLink, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

const MapboxTokenForm = () => {
  const { setMapboxToken } = useMapboxToken();
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token.trim()) {
      setError('Please enter a valid Mapbox token');
      return;
    }

    // Basic validation - Mapbox tokens typically start with 'pk.'
    if (!token.trim().startsWith('pk.')) {
      setError('Mapbox public tokens should start with "pk."');
      return;
    }

    setError(null);
    setMapboxToken(token.trim());
  };

  return (
    <div className="w-full h-full flex items-center justify-center bg-black">
      <Card className="w-full max-w-md bg-background/95 backdrop-blur-sm">
        <CardHeader>
          <CardTitle>Mapbox Token Required</CardTitle>
          <CardDescription>
            A Mapbox public access token is required to display the security map.
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <p className="text-sm text-muted-foreground">
              To get a Mapbox token:
            </p>
            <ol className="text-sm text-muted-foreground list-decimal pl-4 space-y-1">
              <li>Go to <a href="https://mapbox.com" target="_blank" rel="noopener noreferrer" className="text-primary underline inline-flex items-center">Mapbox.com <ExternalLink className="h-3 w-3 ml-1" /></a></li>
              <li>Create an account or sign in</li>
              <li>Navigate to the Tokens section in your account dashboard</li>
              <li>Copy your public access token</li>
            </ol>
            <div className="space-y-2">
              <label htmlFor="mapbox-token" className="text-sm font-medium">
                Mapbox Public Access Token
              </label>
              <Input
                id="mapbox-token"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="pk.eyJ1IjoieW91..."
                className="w-full"
                required
              />
              <p className="text-xs text-muted-foreground">
                Your token will be stored locally on your device.
              </p>
            </div>
          </CardContent>
          <CardFooter>
            <Button type="submit" className="w-full">
              Save Token
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default MapboxTokenForm;
